<template>
  <div class="content-block">
    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-operaciones">
      <DxColumn data-field="CodigoSecuencial" :caption="$t('operaciones.grid.id')" format="0000000000"></DxColumn>
      <DxColumn data-field="FechaAlta" :caption="$t('operaciones.grid.fecha_de_venta')" data-type="date"
        format="dd/MM/yyyy"></DxColumn>
      <DxColumn v-if="puedoVerAgente" data-field="NombreAgente" :caption="$t('operaciones.grid.agente')">
      </DxColumn>
      <DxColumn data-field="Comercializadora" :caption="$t('operaciones.grid.comercializadora')"></DxColumn>
      <DxColumn data-field="DocumentoTitular" :caption="$t('operaciones.grid.dni')"></DxColumn>
      <DxColumn data-field="CodigoEstado" :caption="$t('operaciones.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TieneServiciosAdicionales" :caption="$t('operaciones.grid.tiene_servicios_adicionales')"
        cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TarifaAccesoLuz" :caption="$t('operaciones.grid.tarifa_de_acceso_luz')"></DxColumn>
      <DxColumn data-field="CupsLuz" :caption="$t('operaciones.grid.cups_luz')"></DxColumn>
      <DxColumn data-field="TarifaAccesoGas" :caption="$t('operaciones.grid.tarifa_de_acceso_gas')"></DxColumn>
      <DxColumn data-field="CupsGas" :caption="$t('operaciones.grid.cups_gas')"></DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Comision" :caption="$t('operaciones.grid.comision')"
        data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Retrocomision" :caption="$t('operaciones.grid.retrocomision')"
        data-type="number" :calculate-cell-value="formatRetrocomision" :calculate-filter-expression="getRetrocomisionFilter">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="FechaModificacion" :caption="$t('operaciones.grid.fecha_ultima_modificacion')"
        data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn type="buttons" :caption="$t('operaciones.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          icon: 'fa-regular fa-comment-dots text-success',
          hint: $t('operaciones.grid.ir_a_chat'),
          onClick: (e: any) => irAChat(e)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          icon: 'fa-regular fa-magnifying-glass text-success',
          hint: $t('operaciones.grid.ver_detalle'),
          onClick: (e: any) => verDetalle(e)
        }
      ]"/>
      <DxColumnFixing :enabled="true" />

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-operacion ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`operaciones.estado.${data.value}`) }}
        </div>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: operacion }">
        <operaciones-grid-detail :template-data="operacion" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxColumn,
  DxDataGrid,
  DxFormat,
  DxLookup,
  DxMasterDetail,
  DxColumnFixing,
} from "devextreme-vue/data-grid";
import {
  currencyFormat,
  getDefaultGridProperties,
  getLookup,
  getRetrocomisionFilter,
  getColumnLookupBoolean
} from "@helpers/grid-helper";
import { useModalService } from "@services/modal-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import type CustomStore from "devextreme/data/custom_store";
import { computed, onMounted, ref } from "vue";
import OperacionesGridDetail from "@/components/operaciones-energia/OperacionesEnergiaGridDetail.vue";
import OperacionesResumen from "@/components/operaciones-energia/OperacionesEnergiaResumen.vue";
import type DevExpress from "devextreme";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { OperacionEnergia } from "@/components/nueva-venta/models/OperacionEnergia";
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();
const intl = useIntl();
const autho = useAuthorization();

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: await api.toStandardApiFunction("getOperacionesEnergiaForGrid", { showLoading: false })
  });
});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let title = `${intl.i18n.t("operaciones.detalle.titulo")} ${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}`;

  var response = await api.callStandardApi("getOperacionEnergiaDetail", { showLoading: true }, data.row?.data.Id);
  var model = new OperacionEnergia();
  model.createFromOperacionDetail(response);

  await modal.showModal(OperacionesResumen, { data: model }, { title: title });
}

const puedoVerCoste = computed(() => {
  return autho.hasPermission(PermissionsApp.EditAllOperacionesEnergia) || autho.hasPermission(PermissionsApp.ViewDatosEconomicosOperaciones);
})

const puedoVerAgente = computed(() => {
  return autho.hasPermission(PermissionsApp.EditAllOperacionesEnergia) || autho.hasPermission(PermissionsApp.ViewSameCompanyOperaciones) ||
    autho.hasPermission(PermissionsApp.ViewSameDelegacionOperaciones);
})

async function irAChat(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let rutaColaboradores = import.meta.env.VITE_BASE_URL;
  let url = `${rutaColaboradores}/comunicaciones-operacion/${data.row?.data.Id}/${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}/ENERGIA`;
  window.open(url, '_blank');
}

const formatRetrocomision = (rowData: any) => {
  return rowData.Retrocomision ? -Math.abs(rowData.Retrocomision) : rowData.Retrocomision;
};

</script>

<style lang="scss" scoped>


.lupa, .chat {
  color: var(--text-secondary-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}
</style>
<style>
.grid-operaciones .dx-datagrid {
  max-height: calc(100vh - 250px);
}
</style>
